import { sql } from "drizzle-orm";
import { index, pgEnum, pgTable, text, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

import { customTimestamp } from "./utils/types";

export const auditTrailTransactionTypeEnumSchema = z.enum([
  "LOGIN",
  "LOGOUT",
  "CREATE",
  "UPDATE",
  "CREATE_OR_UPDATE",
  "DELETE",
  "GET",
  "LIST",
  "DOWNLOAD",
]);

export const auditTrailTransactionTypeEnum = pgEnum(
  "transaction_type_enum",
  auditTrailTransactionTypeEnumSchema.enum,
);

export const auditTrailUserTypeEnumSchema = z.enum([
  "PATIENT",
  "ADMIN",
  "SYSTEM",
]);
export const auditTrailUserTypeEnum = pgEnum(
  "audit_trail_user_type_enum",
  auditTrailUserTypeEnumSchema.enum,
);

export const auditTrailRecordTypeEnumSchema = z.enum([
  // Table-level record types
  "ADMIN",
  "APPLICATION",
  "APPLICATION_MEMBERS",
  "CONSENT_DOCUMENT",
  "CONSENT_DOCUMENT_SIGNATURES",
  "AUDIT_TRAIL",
  "CLINIC",
  "CLINIC_PATIENTS",
  "COVERAGE_CHECKS",
  "ELIGIBILITY_RESULTS",
  "HOUSEHOLD",
  "HOUSEHOLD_MEMBERS",
  "HOUSEHOLD_MEMBER_DOCUMENTS",
  "HOUSEHOLD_MEMBER_RELATIONSHIPS",
  "PATIENT",
  "PATIENT_CONSENT",
  "PATIENT_UPLOADED_FILES",
  "REDETERMINATION",
  "NPS_SURVEY",
  "CES_SURVEY",
  "WORK_REQUIREMENT",
  "WORK_REQUIREMENT_HOUSEHOLD_MEMBER",
  // Field-level record types for HIPAA sensitive data
  // Will use the patient or household member ID as the recordId for these types
  "SSN", // Standalone HIPAA identifier
  "SIGNATURE", // Biometric identifier (HIPAA identifier #3)
  "MEDICAID_ID", // Health plan beneficiary number (HIPAA identifier #12)
  "MEDICAID_CASE_NUMBER", // Same HIPAA identifier category as MEDICAID_ID
]);
export const auditTrailRecordTypeEnum = pgEnum(
  "audit_trail_record_type_enum",
  auditTrailRecordTypeEnumSchema.enum,
);

export const auditTrailsTable = pgTable(
  "audit_trails",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    requestId: text("request_id").notNull(),
    ipAddress: text("ip_address").notNull(),
    userType: auditTrailUserTypeEnum("user_type").notNull(),
    userId: uuid("user_id").notNull(),
    transactionType:
      auditTrailTransactionTypeEnum("transaction_type").notNull(),
    recordType: auditTrailRecordTypeEnum("record_type"),
    recordId: uuid("record_id"),
    timestamp: customTimestamp("timestamp")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
  },
  table => ({
    requestId: index("audit_trails_request_id_idx").on(table.requestId),
    ipAddressIdx: index("audit_trails_ip_address_idx").on(table.ipAddress),
    userIdIdx: index("audit_trails_user_id_idx").on(table.userId),
    recordIdIdx: index("audit_trails_record_id_idx")
      .on(table.recordId)
      .where(sql`${table.recordId} IS NOT NULL`),
    timestampIdx: index("audit_trails_timestamp_idx").on(table.timestamp),
  }),
);

export const auditTrailsSchema = createSelectSchema(auditTrailsTable, {
  userType: auditTrailUserTypeEnumSchema,
});
export type AuditTrailsType = z.infer<typeof auditTrailsSchema>;

export const auditTrailsInsertSchema = createInsertSchema(auditTrailsTable, {
  ipAddress: z.union([z.ipv4(), z.ipv6()]),
  userType: auditTrailUserTypeEnumSchema,
  userId: z.uuid(),
  recordType: auditTrailRecordTypeEnumSchema.nullish(),
  recordId: z.uuid().nullish(),
  transactionType: auditTrailTransactionTypeEnumSchema,
  timestamp: z.iso.datetime().optional(),
});
export type AuditTrailsInsertType = z.infer<typeof auditTrailsInsertSchema>;

export const auditTrailAuthEventSchema =
  auditTrailTransactionTypeEnumSchema.extract(["LOGIN", "LOGOUT"]);
export type AuditTrailAuthEventType = z.infer<typeof auditTrailAuthEventSchema>;

export const auditTrailDownloadEventRecordTypeSchema =
  auditTrailRecordTypeEnumSchema.extract([
    "CONSENT_DOCUMENT",
    "HOUSEHOLD_MEMBER_DOCUMENTS",
  ]);
export type AuditTrailDownloadEventRecordType = z.infer<
  typeof auditTrailDownloadEventRecordTypeSchema
>;
